import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Avatar, Button, useTheme, Dialog, DialogTitle, DialogContent,
  Snackbar, Alert, TextField, Switch, FormControlLabel, CircularProgress,
  DialogActions, Divider
} from '@mui/material';
import { styled } from '@mui/system';
import { motion, AnimatePresence } from 'framer-motion';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import EmailIcon from '@mui/icons-material/Email';
import StorageIcon from '@mui/icons-material/Storage';
import HelpIcon from '@mui/icons-material/Help';
import StarIcon from '@mui/icons-material/Star';
import VerifiedIcon from '@mui/icons-material/Verified';
import InsightsIcon from '@mui/icons-material/Insights';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import WarningIcon from '@mui/icons-material/Warning';

import {
  checkProStatus,
  getUserStatistics,
  clearChatHistory,
  exportUserData,
  updateUserEmail,
  getUserProfile,
  updateUserProfile,
  uploadProfilePhoto,
  updatePrivacySettings,
  getPrivacySettings,
  deleteUserAccount
} from '../services/AGTHallService';
import AppleSpinner from '../../common/AppleSpinner';

// Consistent accent color
const ACCENT_COLOR = '#0385FF';

// Main container with scroll
const ScrollContainer = styled(Box)(({ theme }) => ({
  height: '100vh',
  overflow: 'auto',
  padding: theme.spacing(2),
  paddingBottom: theme.spacing(4),
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.mode === 'dark' ? '#3c3c3e' : '#C7C7CC',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: theme.palette.mode === 'dark' ? '#636366' : '#AEAEB2',
  },
}));

// Styled components con animaciones
const ProfileContainer = styled(motion.div)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark' ? '#1c1c1e' : '#fff'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    borderRadius: '16px',
    color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
    maxWidth: '500px',
    width: '90%',
    margin: '0 auto',
    marginBottom: theme.spacing(3),
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    boxShadow: theme.palette.mode === 'dark'
      ? 'none'
      : '0px 2px 12px rgba(0, 0, 0, 0.05)',
    [theme.breakpoints.down('sm')]: {
      width: '95%',
    }
  };
});

const ProfileHeader = styled(motion.div)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark' ? '#2c2c2e' : '#F2F2F7'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    borderRadius: '16px',
    marginBottom: theme.spacing(2),
    cursor: 'pointer',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    '&:hover': {
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.5)'
        : (theme.palette.mode === 'dark' ? '#3c3c3e' : '#E9E9EB'),
      transform: 'translateY(-2px)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 4px 12px rgba(0, 0, 0, 0.3)'
        : '0 4px 12px rgba(0, 0, 0, 0.08)',
    }
  };
});

const ProfileImage = styled(Avatar)(({ theme }) => ({
  width: 60,
  height: 60,
  marginRight: theme.spacing(2),
  border: theme.palette.mode === 'dark'
    ? 'none'
    : `1px solid rgba(0, 0, 0, 0.05)`,
  [theme.breakpoints.down('sm')]: {
    width: 50,
    height: 50,
  }
}));

const SettingsList = styled(motion.div)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark' ? '#2c2c2e' : '#F2F2F7'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    borderRadius: '16px',
    marginBottom: theme.spacing(2),
    padding: 0,
    overflow: 'hidden',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
  };
});

const SettingsListItem = styled(motion.div)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: theme.spacing(2),
    transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    '&[data-last-item="true"]': {
      borderBottom: 'none',
    },
    '&[data-last-item="false"]': {
      borderBottom: `0.5px solid ${theme.palette.mode === 'dark' ? '#3c3c3e' : '#E5E5EA'}`,
    },
    '&:hover': {
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.5)'
        : (theme.palette.mode === 'dark' ? '#3c3c3e' : '#E9E9EB'),
    }
  };
});

const IconWrapper = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#E9E9EB'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '12px',
    flexShrink: 0,
    transition: 'transform 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    '& .MuiSvgIcon-root': {
      color: isWallpaperMode ? '#FFFFFF' : ACCENT_COLOR,
      fontSize: '16px',
    }
  };
});

const SignOutButton = styled(motion.button)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark' ? '#2c2c2e' : '#F2F2F7'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    color: '#FF453A',
    borderRadius: '16px',
    padding: theme.spacing(1.5),
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '1rem',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    cursor: 'pointer',
    width: '100%',
    transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    '&:hover': {
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.5)'
        : (theme.palette.mode === 'dark' ? '#3c3c3e' : '#E9E9EB'),
      transform: 'translateY(-2px)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 4px 12px rgba(0, 0, 0, 0.3)'
        : '0 4px 12px rgba(0, 0, 0, 0.08)',
    }
  };
});

const StyledChevronIcon = styled(ChevronRightIcon)(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? '#636366' : '#C7C7CC',
  fontSize: '1.2rem',
}));

const SectionTitle = styled(motion.div)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(2),
  color: theme.palette.mode === 'dark' ? '#8E8E93' : '#8E8E93',
  paddingLeft: theme.spacing(1),
  fontSize: '0.85rem',
  fontWeight: 500,
  letterSpacing: '0.5px',
}));

// Custom dialog component con animaciones - estilo macOS
const StyledDialog = styled(Dialog)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    '& .MuiDialog-paper': {
      borderRadius: 20,
      backgroundColor: isWallpaperMode
        ? 'rgba(55, 55, 55, 0.1)'
        : (theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF'),
      backdropFilter: 'blur(40px)',
      WebkitBackdropFilter: 'blur(40px)',
      border: isWallpaperMode
        ? '1px solid rgba(55, 55, 55, 0.1)'
        : (theme.palette.mode === 'dark'
          ? '1px solid rgba(255, 255, 255, 0.1)'
          : 'none'),
      maxWidth: '500px',
      width: '95%',
      boxShadow: theme.palette.mode === 'dark'
        ? '0px 20px 60px rgba(0, 0, 0, 0.6)'
        : '0px 20px 60px rgba(0, 0, 0, 0.2)',
      overflow: 'hidden',
      '@media (max-width: 600px)': {
        width: '100%',
        maxWidth: '100%',
        margin: '16px',
        maxHeight: '90vh',
      }
    },
    '& .MuiBackdrop-root': {
      backgroundColor: 'transparent',
    }
  };
});

const DialogHeader = styled(DialogTitle)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    backgroundColor: 'transparent',
    color: isWallpaperMode
      ? '#FFFFFF'
      : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
    padding: theme.spacing(1.5, 3, 1.5, 3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: 600,
  };
});

// Backdrop animado - estilo macOS
const AnimatedBackdrop = styled(motion.div)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'transparent',
  backdropFilter: 'blur(2px)',
  WebkitBackdropFilter: 'blur(2px)',
  zIndex: 1300,
}));

// Variantes de animación
const containerVariants = {
  hidden: { 
    opacity: 0,
    y: 20
  },
  visible: { 
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { 
    opacity: 0,
    x: -20
  },
  visible: { 
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

// const sectionVariants = {
//   hidden: {
//     opacity: 0,
//     y: 10
//   },
//   visible: (index) => ({
//     opacity: 1,
//     y: 0,
//     transition: {
//       delay: index * 0.1,
//       duration: 0.4,
//       ease: [0.25, 0.46, 0.45, 0.94]
//     }
//   })
// };

const dialogVariants = {
  hidden: { 
    opacity: 0,
    scale: 0.95,
    y: 20
  },
  visible: { 
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: {
      duration: 0.2,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const backdropVariants = {
  hidden: { 
    opacity: 0,
    backdropFilter: 'blur(0px)'
  },
  visible: { 
    opacity: 1,
    backdropFilter: 'blur(10px)',
    transition: {
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  },
  exit: {
    opacity: 0,
    backdropFilter: 'blur(0px)',
    transition: {
      duration: 0.2,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const listItemHover = {
  scale: 1.01,
  transition: {
    duration: 0.2,
    ease: [0.25, 0.46, 0.45, 0.94]
  }
};

const listItemTap = {
  scale: 0.98,
  transition: {
    duration: 0.1,
    ease: [0.25, 0.46, 0.45, 0.94]
  }
};

const buttonTap = {
  scale: 0.98,
  transition: {
    duration: 0.1,
    ease: [0.25, 0.46, 0.45, 0.94]
  }
};

// Styled TextField con estilo macOS
const MacOSTextField = styled(TextField)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.03)',
      transition: 'all 0.2s ease',
      '& fieldset': {
        borderColor: theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.1)'
          : 'rgba(0, 0, 0, 0.1)',
        borderRadius: 12,
      },
      '&:hover fieldset': {
        borderColor: isWallpaperMode
          ? 'rgba(255, 255, 255, 0.3)'
          : ACCENT_COLOR,
      },
      '&.Mui-focused fieldset': {
        borderColor: isWallpaperMode
          ? '#FFFFFF'
          : ACCENT_COLOR,
        borderWidth: 2,
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.6)'
        : 'rgba(0, 0, 0, 0.6)',
      '&.Mui-focused': {
        color: isWallpaperMode
          ? '#FFFFFF'
          : ACCENT_COLOR,
      },
    },
    '& .MuiInputBase-input': {
      color: isWallpaperMode
        ? '#FFFFFF'
        : theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
    },
  };
});

// iOS-style Switch
const IOSSwitch = styled(Switch)(({ theme }) => ({
  width: 51,
  height: 31,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(20px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: ACCENT_COLOR,
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: ACCENT_COLOR,
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.mode === 'light'
        ? theme.palette.grey[100]
        : theme.palette.grey[600],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 27,
    height: 27,
    boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.2)',
  },
  '& .MuiSwitch-track': {
    borderRadius: 31 / 2,
    backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 200,
    }),
  },
}));

const ProfileDashboard = ({ user, onLogout, onEditProfile, onNavigate }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [userProfile, setUserProfile] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Email settings state
  const [emailSettings, setEmailSettings] = useState({
    email: user?.email || '',
    emailNotifications: false,
    loading: false,
    error: ''
  });

  // Privacy settings state
  const [privacySettings, setPrivacySettings] = useState({
    dataSharing: true,
    activityHistory: true,
    analyticsEnabled: true,
    loading: false
  });

  // Data management state
  const [dataManagement, setDataManagement] = useState({
    keepStarred: true,
    loading: false,
    dataSummary: { totalChats: 0, totalMessages: 0, totalProjects: 0 }
  });

  // Statistics state
  const [statistics, setStatistics] = useState({
    loading: false,
    timeRange: 'month',
    status: null, // 'pending', 'completed', 'error', null
    message: null,
    cached: false,
    lastCalculated: null,
    data: {
      totalChats: 0,
      totalMessages: 0,
      averageMessagesPerChat: 0,
      mostActiveDay: null,
      mostUsedApps: []
    }
  });

  // Edit profile state
  const [editProfile, setEditProfile] = useState({
    open: false,
    displayName: '',
    email: '',
    photoFile: null,
    photoPreview: '',
    loading: false,
    error: ''
  });

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    confirmText: '',
    cancelText: 'Cancel',
    onConfirm: null,
    isDangerous: false
  });

  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user || !user.uid) return;

      setLoading(true);
      try {
        const [proStatus, fullProfile] = await Promise.all([
          checkProStatus(user.uid),
          getUserProfile(user.uid)
        ]);

        setUserProfile({
          isPro: proStatus.isPro,
          proExpirationDate: proStatus.proExpirationDate,
          email: fullProfile.profile?.email || user.email,
          emailNotifications: fullProfile.profile?.emailNotifications ?? true,
          displayName: fullProfile.profile?.displayName || user.displayName,
          photoURL: fullProfile.profile?.photoURL || user.photoURL
        });

        // Update email settings state with actual backend values
        setEmailSettings({
          email: fullProfile.profile?.email || user.email || '',
          emailNotifications: fullProfile.profile?.emailNotifications ?? true,
          loading: false,
          error: ''
        });
      } catch (error) {
        console.error("Error loading user profile:", error);
        setSnackbar({
          open: true,
          message: 'Error loading account information',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [user]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handleOpenDialog = (dialogName) => {
    setOpenDialog(dialogName);
  };

  const handleCloseDialog = () => {
    // Reset statistics to ensure fresh data on next open
    if (openDialog === 'stats') {
      setStatistics(prev => ({
        ...prev,
        data: {
          totalChats: 0,
          totalMessages: 0,
          averageMessagesPerChat: 0,
          mostActiveDay: null,
          mostUsedApps: []
        }
      }));
    }
    setOpenDialog('');
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({...snackbar, open: false});
  };

  // ============================================================================
  // EMAIL SETTINGS HANDLERS
  // ============================================================================

  const handleUpdateEmail = async (newEmail) => {
    setEmailSettings({ ...emailSettings, loading: true, error: '' });

    try {
      await updateUserEmail(user.uid, newEmail);

      // Update both emailSettings and userProfile to reflect the change in UI
      setEmailSettings({ ...emailSettings, email: newEmail, loading: false });
      setUserProfile({ ...userProfile, email: newEmail });

      setSnackbar({
        open: true,
        message: 'Email updated successfully!',
        severity: 'success'
      });
      handleCloseDialog();
    } catch (error) {
      setEmailSettings({
        ...emailSettings,
        loading: false,
        error: error.message
      });
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error'
      });
    }
  };

  const handleToggleEmailNotifications = async (enabled) => {
    try {
      setEmailSettings({ ...emailSettings, emailNotifications: enabled });
      // API call already exists in AGTHallService
      setSnackbar({
        open: true,
        message: `Email notifications ${enabled ? 'enabled' : 'disabled'}`,
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to update notification settings',
        severity: 'error'
      });
    }
  };

  // ============================================================================
  // PRIVACY SETTINGS HANDLERS
  // ============================================================================

  const handleLoadPrivacySettings = async () => {
    if (!user?.uid) return;

    setPrivacySettings({ ...privacySettings, loading: true });
    try {
      const settings = await getPrivacySettings(user.uid);
      setPrivacySettings({
        ...settings,
        loading: false
      });
    } catch (error) {
      setPrivacySettings({ ...privacySettings, loading: false });
    }
  };

  const handleUpdatePrivacySettings = async (settings) => {
    setPrivacySettings({ ...privacySettings, loading: true });

    try {
      await updatePrivacySettings(user.uid, settings);
      setPrivacySettings({ ...settings, loading: false });
      setSnackbar({
        open: true,
        message: 'Privacy settings updated!',
        severity: 'success'
      });
      handleCloseDialog();
    } catch (error) {
      setPrivacySettings({ ...privacySettings, loading: false });
      setSnackbar({
        open: true,
        message: 'Failed to update privacy settings',
        severity: 'error'
      });
    }
  };

  const handleDeleteAccount = () => {
    setConfirmDialog({
      open: true,
      title: 'Delete Account',
      message: 'This action cannot be undone. All your data will be permanently deleted.',
      confirmText: 'DELETE_MY_ACCOUNT',
      cancelText: 'Cancel',
      isDangerous: true,
      onConfirm: async (confirmation) => {
        try {
          await deleteUserAccount(user.uid, confirmation);
          setSnackbar({
            open: true,
            message: 'Account deleted. Logging out...',
            severity: 'info'
          });
          // Wait a bit then logout
          setTimeout(() => {
            onLogout();
          }, 2000);
        } catch (error) {
          setSnackbar({
            open: true,
            message: error.message,
            severity: 'error'
          });
        }
      }
    });
  };

  // ============================================================================
  // DATA MANAGEMENT HANDLERS
  // ============================================================================

  const handleExportData = async () => {
    setDataManagement({ ...dataManagement, loading: true });

    try {
      const data = await exportUserData(user.uid);

      // Create blob and download
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `agt-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setDataManagement({ ...dataManagement, loading: false });
      setSnackbar({
        open: true,
        message: 'Data exported successfully!',
        severity: 'success'
      });
    } catch (error) {
      setDataManagement({ ...dataManagement, loading: false });
      setSnackbar({
        open: true,
        message: 'Failed to export data',
        severity: 'error'
      });
    }
  };

  const handleClearHistory = () => {
    setConfirmDialog({
      open: true,
      title: 'Clear Chat History',
      message: `Are you sure you want to clear your chat history? ${dataManagement.keepStarred ? 'Starred chats will be preserved.' : 'All chats will be deleted.'}`,
      confirmText: 'CLEAR_HISTORY',
      cancelText: 'Cancel',
      isDangerous: true,
      onConfirm: async () => {
        setDataManagement({ ...dataManagement, loading: true });
        try {
          await clearChatHistory(user.uid, dataManagement.keepStarred);
          setDataManagement({
            ...dataManagement,
            loading: false,
            dataSummary: { totalChats: 0, totalMessages: 0, totalProjects: 0 }
          });
          setSnackbar({
            open: true,
            message: 'Chat history cleared!',
            severity: 'success'
          });
          handleCloseDialog();
        } catch (error) {
          setDataManagement({ ...dataManagement, loading: false });
          setSnackbar({
            open: true,
            message: 'Failed to clear history',
            severity: 'error'
          });
        }
      }
    });
  };

  // ============================================================================
  // STATISTICS HANDLERS
  // ============================================================================

  const handleLoadStatistics = useCallback(async (forceRefresh = false) => {
    if (!user?.uid) return;

    setStatistics(prev => ({ ...prev, loading: true, status: null }));
    try {
      const stats = await getUserStatistics(user.uid, forceRefresh);

      // Si status === 'pending', las estadísticas están siendo calculadas
      if (stats.status === 'pending') {
        setStatistics(prev => ({
          ...prev,
          loading: false,
          status: 'pending',
          message: stats.message,
          startedAt: stats.startedAt
        }));
        return;
      }

      // Si hay datos, mostrarlos
      setStatistics(prev => ({
        ...prev,
        loading: false,
        status: 'completed',
        cached: stats.cached || false,
        lastCalculated: stats.lastCalculated || null,
        data: {
          totalChats: stats.totalChats || 0,
          totalMessages: stats.totalMessages || 0,
          averageMessagesPerChat: stats.averageMessagesPerChat || 0,
          mostActiveDay: stats.mostActiveDay || null,
          mostUsedApps: stats.mostUsedApps || []
        }
      }));
    } catch (error) {
      setStatistics(prev => ({ ...prev, loading: false, status: 'error' }));
      setSnackbar({
        open: true,
        message: 'Failed to load statistics',
        severity: 'error'
      });
    }
  }, [user?.uid]);

  const handleRefreshStatistics = () => {
    handleLoadStatistics(true); // Pasar true para forceRefresh
  };

  const handleChangeTimeRange = (range) => {
    setStatistics({ ...statistics, timeRange: range });
    // In a real implementation, this would trigger a new API call with the time range
  };

  // ============================================================================
  // EDIT PROFILE HANDLERS
  // ============================================================================

  const handleEditProfileOpen = () => {
    setEditProfile({
      ...editProfile,
      open: true,
      displayName: userProfile?.displayName || user?.displayName || '',
      email: userProfile?.email || user?.email || '',
      photoPreview: userProfile?.photoURL || user?.photoURL || ''
    });
  };

  const handleEditProfileClose = () => {
    setEditProfile({
      ...editProfile,
      open: false,
      photoFile: null,
      error: ''
    });
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setEditProfile({
        ...editProfile,
        error: 'Please select an image file'
      });
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setEditProfile({
        ...editProfile,
        error: 'Image must be less than 5MB'
      });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setEditProfile({
        ...editProfile,
        photoFile: file,
        photoPreview: e.target.result,
        error: ''
      });
    };
    reader.readAsDataURL(file);
  };

  const handleUpdateProfile = async () => {
    setEditProfile({ ...editProfile, loading: true, error: '' });

    try {
      let photoURL = user?.photoURL;

      // Upload photo if changed
      if (editProfile.photoFile) {
        const uploadResult = await uploadProfilePhoto(user.uid, editProfile.photoFile);
        photoURL = uploadResult.photoURL;
      }

      // Update profile
      await updateUserProfile(user.uid, {
        displayName: editProfile.displayName,
        photoURL: photoURL
      });

      // Update userProfile state to reflect changes immediately
      setUserProfile({
        ...userProfile,
        displayName: editProfile.displayName,
        photoURL: photoURL
      });

      setEditProfile({ ...editProfile, loading: false });
      setSnackbar({
        open: true,
        message: 'Profile updated successfully!',
        severity: 'success'
      });
      handleEditProfileClose();
    } catch (error) {
      setEditProfile({
        ...editProfile,
        loading: false,
        error: error.message
      });
      setSnackbar({
        open: true,
        message: 'Failed to update profile',
        severity: 'error'
      });
    }
  };

  // ============================================================================
  // CONFIRMATION DIALOG HANDLERS
  // ============================================================================

  const handleConfirmDialogClose = () => {
    setConfirmDialog({
      ...confirmDialog,
      open: false
    });
  };

  const handleConfirmDialogConfirm = async (inputValue) => {
    if (confirmDialog.onConfirm) {
      await confirmDialog.onConfirm(inputValue);
    }
    handleConfirmDialogClose();
  };

  // Load privacy settings when dialog opens
  useEffect(() => {
    if (openDialog === 'privacy') {
      handleLoadPrivacySettings();
    }
  }, [openDialog]);

  // Load statistics when dialog opens
  useEffect(() => {
    if (openDialog === 'stats') {
      handleLoadStatistics();
    }
  }, [openDialog, handleLoadStatistics]);

  if (loading) {
    return (
      <Box sx={{
        bgcolor: 'inherit',
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <AppleSpinner sizeDesktop={60} sizeMobile={40} />
      </Box>
    );
  }

  return (
    <ScrollContainer>
      <ProfileContainer
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Profile header with image and name */}
        <ProfileHeader
          variants={itemVariants}
          onClick={handleEditProfileOpen}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.98 }}
        >
          <ProfileImage src={userProfile?.photoURL || user?.photoURL} alt={userProfile?.displayName || user?.displayName}>
            {!(userProfile?.photoURL || user?.photoURL) && <PersonIcon sx={{ color: ACCENT_COLOR }} />}
          </ProfileImage>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{
              fontWeight: 600,
              color: isDarkMode ? '#ffffff' : '#000000',
              fontSize: '1rem'
            }}>
              {userProfile?.displayName || user?.displayName || 'User'}
            </Typography>
            <Typography variant="body2" sx={{ 
              color: isDarkMode ? '#ebebf0' : '#8E8E93',
              fontSize: '0.9rem'
            }}>
              {user?.email || 'AGT Profile'}
            </Typography>
          </Box>
          <StyledChevronIcon />
        </ProfileHeader>

        {/* Subscription section */}
        <SectionTitle 
          variants={itemVariants}
          custom={0}
        >
          Subscription
        </SectionTitle>
        <SettingsList variants={itemVariants}>
          <SettingsListItem 
            data-last-item="true" 
            onClick={() => handleOpenDialog('subscription')}
            whileHover={listItemHover}
            whileTap={listItemTap}
          >
            <IconWrapper>
              <StarIcon sx={{ color: userProfile.isPro ? '#FFD700' : ACCENT_COLOR }} />
            </IconWrapper>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{
                color: isDarkMode ? '#ffffff' : '#000000',
                fontWeight: 500,
                fontSize: '0.95rem'
              }}>
                {userProfile.isPro ? "AGT Pro" : "Free Plan"}
              </Typography>
              <Typography sx={{
                color: isDarkMode ? '#8E8E93' : '#8E8E93',
                fontSize: '0.85rem'
              }}>
                {userProfile.isPro 
                  ? `Expires: ${formatDate(userProfile.proExpirationDate)}` 
                  : "Upgrade to Pro for more features"}
              </Typography>
            </Box>
            <Box sx={{ ml: 2 }}>
              {userProfile.isPro ? <VerifiedIcon color="primary" /> : <StyledChevronIcon />}
            </Box>
          </SettingsListItem>
        </SettingsList>

        {/* Account & Privacy section */}
        <SectionTitle variants={itemVariants} custom={1}>
          Account & Privacy
        </SectionTitle>
        <SettingsList variants={itemVariants}>
          <SettingsListItem 
            data-last-item="false" 
            onClick={() => handleOpenDialog('email')}
            whileHover={listItemHover}
            whileTap={listItemTap}
          >
            <IconWrapper>
              <EmailIcon />
            </IconWrapper>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{
                color: isDarkMode ? '#ffffff' : '#000000',
                fontWeight: 500,
                fontSize: '0.95rem'
              }}>
                Email Settings
              </Typography>
              <Typography sx={{
                color: isDarkMode ? '#8E8E93' : '#8E8E93',
                fontSize: '0.85rem'
              }}>
                {userProfile?.email || user?.email || "No email set"}
              </Typography>
            </Box>
            <Box sx={{ ml: 2 }}>
              <StyledChevronIcon />
            </Box>
          </SettingsListItem>

          <SettingsListItem 
            data-last-item="false" 
            onClick={() => handleOpenDialog('privacy')}
            whileHover={listItemHover}
            whileTap={listItemTap}
          >
            <IconWrapper>
              <SecurityIcon />
            </IconWrapper>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{
                color: isDarkMode ? '#ffffff' : '#000000',
                fontWeight: 500,
                fontSize: '0.95rem'
              }}>
                Privacy Settings
              </Typography>
              <Typography sx={{
                color: isDarkMode ? '#8E8E93' : '#8E8E93',
                fontSize: '0.85rem'
              }}>
                Control data sharing and storage
              </Typography>
            </Box>
            <Box sx={{ ml: 2 }}>
              <StyledChevronIcon />
            </Box>
          </SettingsListItem>

          <SettingsListItem 
            data-last-item="true" 
            onClick={() => handleOpenDialog('data')}
            whileHover={listItemHover}
            whileTap={listItemTap}
          >
            <IconWrapper>
              <StorageIcon />
            </IconWrapper>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{
                color: isDarkMode ? '#ffffff' : '#000000',
                fontWeight: 500,
                fontSize: '0.95rem'
              }}>
                Data Management
              </Typography>
              <Typography sx={{
                color: isDarkMode ? '#8E8E93' : '#8E8E93',
                fontSize: '0.85rem'
              }}>
                Clear history, export chats
              </Typography>
            </Box>
            <Box sx={{ ml: 2 }}>
              <StyledChevronIcon />
            </Box>
          </SettingsListItem>
        </SettingsList>

        {/* Support section */}
        <SectionTitle variants={itemVariants} custom={2}>
          Support
        </SectionTitle>
        <SettingsList variants={itemVariants}>
          <SettingsListItem 
            data-last-item="false" 
            onClick={() => window.open('https://directiveai.org/support', '_blank')}
            whileHover={listItemHover}
            whileTap={listItemTap}
          >
            <IconWrapper>
              <HelpIcon />
            </IconWrapper>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{
                color: isDarkMode ? '#ffffff' : '#000000',
                fontWeight: 500,
                fontSize: '0.95rem'
              }}>
                Help & Support
              </Typography>
              <Typography sx={{
                color: isDarkMode ? '#8E8E93' : '#8E8E93',
                fontSize: '0.85rem'
              }}>
                Documentation, tutorials, contact
              </Typography>
            </Box>
            <Box sx={{ ml: 2 }}>
              <StyledChevronIcon />
            </Box>
          </SettingsListItem>

          <SettingsListItem 
            data-last-item="true" 
            onClick={() => handleOpenDialog('stats')}
            whileHover={listItemHover}
            whileTap={listItemTap}
          >
            <IconWrapper>
              <InsightsIcon />
            </IconWrapper>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{
                color: isDarkMode ? '#ffffff' : '#000000',
                fontWeight: 500,
                fontSize: '0.95rem'
              }}>
                Usage Statistics
              </Typography>
              <Typography sx={{
                color: isDarkMode ? '#8E8E93' : '#8E8E93',
                fontSize: '0.85rem'
              }}>
                View your activity and usage
              </Typography>
            </Box>
            <Box sx={{ ml: 2 }}>
              <StyledChevronIcon />
            </Box>
          </SettingsListItem>
        </SettingsList>

        {/* Sign out button */}
        <SignOutButton 
          onClick={onLogout}
          whileTap={buttonTap}
          variants={itemVariants}
        >
          Sign Out
        </SignOutButton>
      </ProfileContainer>

      {/* Subscription dialog con animaciones */}
      <AnimatePresence>
        {openDialog === 'subscription' && (
          <>
            <AnimatedBackdrop
              variants={backdropVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={handleCloseDialog}
            />
            <StyledDialog
              open={true}
              onClose={handleCloseDialog}
              maxWidth="sm"
              fullWidth
              BackdropProps={{ invisible: true }}
              PaperProps={{
                component: motion.div,
                variants: dialogVariants,
                initial: "hidden",
                animate: "visible",
                exit: "exit"
              }}
            >
              <DialogHeader>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Subscription
                </Typography>
                <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                  <IconButton
                    onClick={handleCloseDialog}
                    edge="end"
                    sx={{
                      color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.6)',
                      '&:hover': {
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                      }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </motion.div>
              </DialogHeader>
              <DialogContent sx={{ p: 3, pt: 4, backgroundColor: 'transparent' }}>
                {userProfile.isPro ? (
                  <Box>
                    <Typography variant="h6" sx={{
                      fontWeight: 600,
                      color: theme.palette.actualMode === 'wallpaper'
                        ? '#FFFFFF'
                        : (isDarkMode ? '#FFFFFF' : '#000000')
                    }}>
                      AGT Pro
                    </Typography>
                    <Typography variant="body1" sx={{
                      mb: 2,
                      color: theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(255, 255, 255, 0.85)'
                        : (isDarkMode ? '#EBEBF0' : '#3C3C43')
                    }}>
                      Your subscription is active until {formatDate(userProfile.proExpirationDate)}
                    </Typography>
                    <Box sx={{
                      bgcolor: theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(255, 255, 255, 0.1)'
                        : (isDarkMode ? '#2c2c2e' : '#F2F2F7'),
                      p: 2,
                      borderRadius: '12px',
                      mb: 2,
                      border: theme.palette.actualMode === 'wallpaper' ? '1px solid rgba(255, 255, 255, 0.2)' : 'none'
                    }}>
                      <Typography variant="subtitle1" sx={{
                        fontWeight: 500,
                        color: theme.palette.actualMode === 'wallpaper'
                          ? '#FFFFFF'
                          : (isDarkMode ? '#FFFFFF' : '#000000')
                      }}>
                        Benefits:
                      </Typography>
                      <Typography component="ul" sx={{
                        pl: 2,
                        color: theme.palette.actualMode === 'wallpaper'
                          ? 'rgba(255, 255, 255, 0.9)'
                          : (isDarkMode ? '#EBEBF0' : '#3C3C43')
                      }}>
                        <li>Access to AGT Ultra and A1 models</li>
                        <li>Higher message limits</li>
                        <li>Custom AI personalities</li>
                        <li>Priority support</li>
                      </Typography>
                    </Box>
                    <motion.div whileTap={buttonTap}>
                      <Button
                        variant="outlined"
                        fullWidth
                        sx={{
                          borderColor: ACCENT_COLOR,
                          color: ACCENT_COLOR,
                          borderRadius: 20,
                          p: 1.5,
                          textTransform: 'none',
                          fontWeight: 500,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: '#0271E0',
                            bgcolor: 'rgba(3, 133, 255, 0.1)',
                            transform: 'translateY(-1px)'
                          }
                        }}
                      >
                        Manage Subscription
                      </Button>
                    </motion.div>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="h6" sx={{
                      fontWeight: 600,
                      color: theme.palette.actualMode === 'wallpaper'
                        ? '#FFFFFF'
                        : (isDarkMode ? '#FFFFFF' : '#000000')
                    }}>
                      Upgrade to AGT Pro
                    </Typography>
                    <Typography variant="body1" sx={{
                      mb: 2,
                      color: theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(255, 255, 255, 0.85)'
                        : (isDarkMode ? '#EBEBF0' : '#3C3C43')
                    }}>
                      Unlock premium features with AGT Pro subscription.
                    </Typography>
                    <Box sx={{
                      bgcolor: theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(255, 255, 255, 0.1)'
                        : (isDarkMode ? '#2c2c2e' : '#F2F2F7'),
                      p: 2,
                      borderRadius: '12px',
                      mb: 2,
                      border: theme.palette.actualMode === 'wallpaper' ? '1px solid rgba(255, 255, 255, 0.2)' : 'none'
                    }}>
                      <Typography variant="subtitle1" sx={{
                        fontWeight: 500,
                        color: theme.palette.actualMode === 'wallpaper'
                          ? '#FFFFFF'
                          : (isDarkMode ? '#FFFFFF' : '#000000')
                      }}>
                        Benefits:
                      </Typography>
                      <Typography component="ul" sx={{
                        pl: 2,
                        color: theme.palette.actualMode === 'wallpaper'
                          ? 'rgba(255, 255, 255, 0.9)'
                          : (isDarkMode ? '#EBEBF0' : '#3C3C43')
                      }}>
                        <li>Access to AGT Ultra and A1 models</li>
                        <li>Higher message limits</li>
                        <li>Custom AI personalities</li>
                        <li>Priority support</li>
                      </Typography>
                    </Box>
                    <Box sx={{
                      bgcolor: theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(255, 255, 255, 0.1)'
                        : (isDarkMode ? '#2c2c2e' : '#F2F2F7'),
                      p: 2,
                      borderRadius: '12px',
                      mb: 3,
                      border: theme.palette.actualMode === 'wallpaper' ? '1px solid rgba(255, 255, 255, 0.2)' : 'none'
                    }}>
                      <Typography variant="subtitle1" sx={{
                        fontWeight: 500,
                        color: theme.palette.actualMode === 'wallpaper'
                          ? '#FFFFFF'
                          : (isDarkMode ? '#FFFFFF' : '#000000'),
                        mb: 1
                      }}>
                        Pricing:
                      </Typography>
                      <Typography variant="h5" sx={{
                        fontWeight: 600,
                        color: theme.palette.actualMode === 'wallpaper'
                          ? '#FFFFFF'
                          : ACCENT_COLOR,
                        mb: 0
                      }}>
                        $19.99 <Typography component="span" variant="body2" sx={{
                          color: theme.palette.actualMode === 'wallpaper'
                            ? 'rgba(255, 255, 255, 0.85)'
                            : (isDarkMode ? '#EBEBF0' : '#3C3C43')
                        }}>/ month</Typography>
                      </Typography>
                    </Box>
                    <motion.div whileTap={buttonTap}>
                      <Button
                        variant="contained"
                        fullWidth
                        sx={{
                          bgcolor: ACCENT_COLOR,
                          color: '#FFFFFF',
                          borderRadius: 20,
                          p: 1.5,
                          textTransform: 'none',
                          fontWeight: 500,
                          boxShadow: 'none',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: '#0271E0',
                            boxShadow: 'none',
                            transform: 'translateY(-1px)'
                          }
                        }}
                      >
                        Upgrade Now
                      </Button>
                    </motion.div>
                  </Box>
                )}
              </DialogContent>
            </StyledDialog>
          </>
        )}

        {/* Email Settings Dialog */}
        {openDialog === 'email' && (
          <>
            <AnimatedBackdrop
              variants={backdropVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={handleCloseDialog}
            />
            <StyledDialog
              open={true}
              onClose={handleCloseDialog}
              maxWidth="sm"
              fullWidth
              BackdropProps={{ invisible: true }}
              PaperProps={{
                component: motion.div,
                variants: dialogVariants,
                initial: "hidden",
                animate: "visible",
                exit: "exit"
              }}
            >
              <DialogHeader>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Email Settings
                </Typography>
                <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                  <IconButton
                    onClick={handleCloseDialog}
                    edge="end"
                    sx={{
                      color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.6)',
                      '&:hover': {
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                      }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </motion.div>
              </DialogHeader>
              <DialogContent sx={{ p: 3, pt: 3, backgroundColor: 'transparent' }}>
                <MacOSTextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={emailSettings.email}
                  onChange={(e) => setEmailSettings({ ...emailSettings, email: e.target.value })}
                  error={!!emailSettings.error}
                  helperText={emailSettings.error}
                  sx={{ mb: 3, mt: 2 }}
                />
                <Box sx={{ mb: 3 }}>
                  <FormControlLabel
                    control={
                      <IOSSwitch
                        checked={emailSettings.emailNotifications}
                        onChange={(e) => handleToggleEmailNotifications(e.target.checked)}
                      />
                    }
                    label="Receive email notifications"
                    labelPlacement="start"
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      ml: 0,
                      mr: 0,
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.95rem',
                        fontWeight: 500,
                        flex: 1
                      }
                    }}
                  />
                  <Typography variant="caption" sx={{
                    display: 'block',
                    mt: 1,
                    color: theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255, 255, 255, 0.7)'
                      : (isDarkMode ? '#8E8E93' : '#3C3C43')
                  }}>
                    When enabled, you will receive notifications about: subscription updates (PRO activation/renewal/cancellation), security alerts (email changes, account deletion), and weekly activity summaries.
                  </Typography>
                  <Typography variant="caption" sx={{
                    display: 'block',
                    mt: 1.5,
                    color: theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255, 255, 255, 0.6)'
                      : (isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.45)')
                  }}>
                    Note: Security alerts are always sent regardless of this setting.
                  </Typography>
                </Box>
                <Box sx={{ mt: 3 }}>
                  <motion.div whileTap={buttonTap}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => handleUpdateEmail(emailSettings.email)}
                      disabled={emailSettings.loading}
                      sx={{
                        bgcolor: ACCENT_COLOR,
                        color: '#FFFFFF',
                        borderRadius: 20,
                        p: 1.5,
                        textTransform: 'none',
                        fontWeight: 500,
                        boxShadow: 'none',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: '#0271E0',
                          boxShadow: 'none',
                          transform: 'translateY(-1px)'
                        }
                      }}
                    >
                      {emailSettings.loading ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
                    </Button>
                  </motion.div>
                </Box>
              </DialogContent>
            </StyledDialog>
          </>
        )}

        {/* Privacy Settings Dialog */}
        {openDialog === 'privacy' && (
          <>
            <AnimatedBackdrop
              variants={backdropVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={handleCloseDialog}
            />
            <StyledDialog
              open={true}
              onClose={handleCloseDialog}
              maxWidth="sm"
              fullWidth
              BackdropProps={{ invisible: true }}
              PaperProps={{
                component: motion.div,
                variants: dialogVariants,
                initial: "hidden",
                animate: "visible",
                exit: "exit"
              }}
            >
              <DialogHeader>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Privacy Settings
                </Typography>
                <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                  <IconButton
                    onClick={handleCloseDialog}
                    edge="end"
                    sx={{
                      color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.6)',
                      '&:hover': {
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                      }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </motion.div>
              </DialogHeader>
              <DialogContent sx={{ p: 3, pt: 4, backgroundColor: 'transparent' }}>
                {privacySettings.loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <FormControlLabel
                        control={
                          <IOSSwitch
                            checked={privacySettings.dataSharing}
                            onChange={(e) => setPrivacySettings({ ...privacySettings, dataSharing: e.target.checked })}
                          />
                        }
                        label="Data Sharing"
                        labelPlacement="start"
                        sx={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'space-between',
                          ml: 0,
                          mr: 0,
                          '& .MuiFormControlLabel-label': {
                            fontSize: '0.95rem',
                            fontWeight: 500,
                            flex: 1
                          }
                        }}
                      />
                      <Typography variant="caption" sx={{
                        display: 'block',
                        mt: 1,
                        color: theme.palette.actualMode === 'wallpaper'
                          ? 'rgba(255, 255, 255, 0.7)'
                          : (isDarkMode ? '#8E8E93' : '#3C3C43')
                      }}>
                        Share anonymous usage data to help improve AGT. Includes: models used, frequency of use, and errors encountered. No personal information or message content is shared.
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <FormControlLabel
                        control={
                          <IOSSwitch
                            checked={privacySettings.activityHistory}
                            onChange={(e) => setPrivacySettings({ ...privacySettings, activityHistory: e.target.checked })}
                          />
                        }
                        label="Activity History"
                        labelPlacement="start"
                        sx={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'space-between',
                          ml: 0,
                          mr: 0,
                          '& .MuiFormControlLabel-label': {
                            fontSize: '0.95rem',
                            fontWeight: 500,
                            flex: 1
                          }
                        }}
                      />
                      <Typography variant="caption" sx={{
                        display: 'block',
                        mt: 1,
                        color: theme.palette.actualMode === 'wallpaper'
                          ? 'rgba(255, 255, 255, 0.7)'
                          : (isDarkMode ? '#8E8E93' : '#3C3C43')
                      }}>
                        Save a log of your actions (create/delete chats, change settings, etc.) to personalize your experience and allow you to review your activity history.
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 3 }}>
                      <FormControlLabel
                        control={
                          <IOSSwitch
                            checked={privacySettings.analyticsEnabled}
                            onChange={(e) => setPrivacySettings({ ...privacySettings, analyticsEnabled: e.target.checked })}
                          />
                        }
                        label="Analytics"
                        labelPlacement="start"
                        sx={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'space-between',
                          ml: 0,
                          mr: 0,
                          '& .MuiFormControlLabel-label': {
                            fontSize: '0.95rem',
                            fontWeight: 500,
                            flex: 1
                          }
                        }}
                      />
                      <Typography variant="caption" sx={{
                        display: 'block',
                        mt: 1,
                        color: theme.palette.actualMode === 'wallpaper'
                          ? 'rgba(255, 255, 255, 0.7)'
                          : (isDarkMode ? '#8E8E93' : '#3C3C43')
                      }}>
                        Enable collection of usage statistics to show you insights about how you use AGT (most used models, active days, app usage, etc.). View statistics in "Usage Statistics" section.
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 3 }} />
                    <motion.div whileTap={buttonTap}>
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<DeleteIcon sx={{ color: '#FFFFFF' }} />}
                        onClick={handleDeleteAccount}
                        sx={{
                          bgcolor: '#FF3B30',
                          color: '#FFFFFF',
                          borderRadius: 20,
                          p: 1.5,
                          textTransform: 'none',
                          fontWeight: 500,
                          mb: 2,
                          boxShadow: 'none',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: '#D70015',
                            boxShadow: 'none',
                            transform: 'translateY(-1px)'
                          }
                        }}
                      >
                        Delete Account
                      </Button>
                    </motion.div>
                    <motion.div whileTap={buttonTap}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => handleUpdatePrivacySettings(privacySettings)}
                        sx={{
                          bgcolor: ACCENT_COLOR,
                          color: '#FFFFFF',
                          borderRadius: 20,
                          p: 1.5,
                          textTransform: 'none',
                          fontWeight: 500,
                          boxShadow: 'none',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: '#0271E0',
                            boxShadow: 'none',
                            transform: 'translateY(-1px)'
                          }
                        }}
                      >
                        Save Changes
                      </Button>
                    </motion.div>
                  </>
                )}
              </DialogContent>
            </StyledDialog>
          </>
        )}

        {/* Data Management Dialog */}
        {openDialog === 'data' && (
          <>
            <AnimatedBackdrop
              variants={backdropVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={handleCloseDialog}
            />
            <StyledDialog
              open={true}
              onClose={handleCloseDialog}
              maxWidth="sm"
              fullWidth
              BackdropProps={{ invisible: true }}
              PaperProps={{
                component: motion.div,
                variants: dialogVariants,
                initial: "hidden",
                animate: "visible",
                exit: "exit"
              }}
            >
              <DialogHeader>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Data Management
                </Typography>
                <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                  <IconButton
                    onClick={handleCloseDialog}
                    edge="end"
                    sx={{
                      color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.6)',
                      '&:hover': {
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                      }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </motion.div>
              </DialogHeader>
              <DialogContent sx={{ p: 3, pt: 4, backgroundColor: 'transparent' }}>
                {/* Export Data */}
                <Typography variant="subtitle1" sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (isDarkMode ? '#FFFFFF' : '#000000')
                }}>
                  Export Data
                </Typography>
                <Typography variant="body2" sx={{
                  color: theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : (isDarkMode ? '#8E8E93' : '#3C3C43'),
                  mb: 2
                }}>
                  Download all your data in JSON format
                </Typography>
                <motion.div whileTap={buttonTap}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<DownloadIcon sx={{ color: '#FFFFFF' }} />}
                    onClick={handleExportData}
                    disabled={dataManagement.loading}
                    sx={{
                      bgcolor: ACCENT_COLOR,
                      color: '#FFFFFF',
                      borderRadius: 20,
                      p: 1.5,
                      textTransform: 'none',
                      fontWeight: 500,
                      mb: 3,
                      boxShadow: 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: '#0271E0',
                        boxShadow: 'none',
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    Download My Data
                  </Button>
                </motion.div>

                {/* Clear History */}
                <Typography variant="subtitle1" sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (isDarkMode ? '#FFFFFF' : '#000000')
                }}>
                  Clear History
                </Typography>
                <Typography variant="body2" sx={{
                  color: theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : (isDarkMode ? '#8E8E93' : '#3C3C43'),
                  mb: 2
                }}>
                  Delete your chat history. You can choose to keep your favorite chats.
                </Typography>
                <FormControlLabel
                  control={
                    <IOSSwitch
                      checked={dataManagement.keepStarred}
                      onChange={(e) => setDataManagement({ ...dataManagement, keepStarred: e.target.checked })}
                    />
                  }
                  label="Keep starred chats"
                  labelPlacement="start"
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    ml: 0,
                    mr: 0,
                    mb: 1,
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.95rem',
                      fontWeight: 500,
                      flex: 1
                    }
                  }}
                />
                <Typography variant="caption" sx={{
                  display: 'block',
                  mt: 1,
                  mb: 2,
                  color: theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : (isDarkMode ? '#8E8E93' : '#3C3C43')
                }}>
                  If enabled, only chats without a star will be deleted. Your favorite (starred) chats will be preserved.
                </Typography>
                <motion.div whileTap={buttonTap}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<DeleteIcon sx={{ color: '#FFFFFF' }} />}
                    onClick={handleClearHistory}
                    disabled={dataManagement.loading}
                    sx={{
                      bgcolor: '#FF3B30',
                      color: '#FFFFFF',
                      borderRadius: 20,
                      p: 1.5,
                      textTransform: 'none',
                      fontWeight: 500,
                      boxShadow: 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: '#D70015',
                        boxShadow: 'none',
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    Clear Chat History
                  </Button>
                </motion.div>
              </DialogContent>
            </StyledDialog>
          </>
        )}

        {/* Usage Statistics Dialog */}
        {openDialog === 'stats' && (
          <>
            <AnimatedBackdrop
              variants={backdropVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={handleCloseDialog}
            />
            <StyledDialog
              open={true}
              onClose={handleCloseDialog}
              maxWidth="sm"
              fullWidth
              BackdropProps={{ invisible: true }}
              PaperProps={{
                component: motion.div,
                variants: dialogVariants,
                initial: "hidden",
                animate: "visible",
                exit: "exit"
              }}
            >
              <DialogHeader>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Usage Statistics
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    onClick={handleRefreshStatistics}
                    size="small"
                    sx={{
                      color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.6)',
                      '&:hover': {
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                      }
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                  <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                    <IconButton
                      onClick={handleCloseDialog}
                      edge="end"
                      sx={{
                        color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.6)',
                        '&:hover': {
                          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                        }
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </motion.div>
                </Box>
              </DialogHeader>
              <DialogContent sx={{ p: 3, pt: 4, backgroundColor: 'transparent' }}>
                {statistics.loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : statistics.status === 'pending' ? (
                  // NUEVO: Mostrar mensaje de espera cuando status === 'pending'
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 3,
                    p: 4
                  }}>
                    <Box sx={{
                      fontSize: 64,
                      color: theme.palette.actualMode === 'wallpaper'
                        ? '#FFFFFF'
                        : ACCENT_COLOR
                    }}>
                      📊
                    </Box>
                    <Typography sx={{
                      fontSize: '1.2rem',
                      fontWeight: 600,
                      textAlign: 'center',
                      color: theme.palette.actualMode === 'wallpaper'
                        ? '#FFFFFF'
                        : (isDarkMode ? '#FFFFFF' : '#000000')
                    }}>
                      Calculating Your Statistics
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.95rem',
                      textAlign: 'center',
                      color: theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(255, 255, 255, 0.7)'
                        : (isDarkMode ? '#8E8E93' : '#3C3C43'),
                      maxWidth: 400
                    }}>
                      {statistics.message || 'Your statistics are being calculated. We will notify you by email when they are ready.'}
                    </Typography>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: 2,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(255, 255, 255, 0.1)'
                        : (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)')
                    }}>
                      <EmailIcon sx={{
                        fontSize: 20,
                        color: theme.palette.actualMode === 'wallpaper'
                          ? 'rgba(255, 255, 255, 0.7)'
                          : ACCENT_COLOR
                      }} />
                      <Typography sx={{
                        fontSize: '0.85rem',
                        color: theme.palette.actualMode === 'wallpaper'
                          ? 'rgba(255, 255, 255, 0.7)'
                          : (isDarkMode ? '#8E8E93' : '#3C3C43')
                      }}>
                        Check your email for notification
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <>
                    {/* Mostrar indicador de caché si aplica */}
                    {statistics.cached && statistics.lastCalculated && (
                      <Box sx={{
                        mb: 2,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: theme.palette.actualMode === 'wallpaper'
                          ? 'rgba(255, 255, 255, 0.08)'
                          : (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'),
                        border: theme.palette.actualMode === 'wallpaper'
                          ? '1px solid rgba(255, 255, 255, 0.15)'
                          : (isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : 'none')
                      }}>
                        <Typography sx={{
                          fontSize: '0.8rem',
                          color: theme.palette.actualMode === 'wallpaper'
                            ? 'rgba(255, 255, 255, 0.6)'
                            : (isDarkMode ? '#8E8E93' : '#3C3C43')
                        }}>
                          Last updated: {new Date(statistics.lastCalculated).toLocaleString()}
                        </Typography>
                      </Box>
                    )}

                    {/* Time Range Segmented Control - macOS style */}
                    <Box sx={{
                      display: 'inline-flex',
                      bgcolor: theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(255, 255, 255, 0.1)'
                        : (isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)'),
                      borderRadius: 2.5,
                      p: 0.5,
                      mb: 3
                    }}>
                      {['week', 'month', 'all'].map((range) => (
                        <Button
                          key={range}
                          onClick={() => handleChangeTimeRange(range)}
                          sx={{
                            minWidth: 70,
                            px: 2,
                            py: 0.5,
                            textTransform: 'capitalize',
                            borderRadius: 2,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: statistics.timeRange === range
                              ? (theme.palette.actualMode === 'wallpaper' ? '#000000' : (isDarkMode ? '#000000' : '#000000'))
                              : (theme.palette.actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.7)' : (isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)')),
                            bgcolor: statistics.timeRange === range ? '#FFFFFF' : 'transparent',
                            boxShadow: statistics.timeRange === range ? '0 1px 3px rgba(0, 0, 0, 0.12)' : 'none',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: statistics.timeRange === range
                                ? '#FFFFFF'
                                : (theme.palette.actualMode === 'wallpaper'
                                  ? 'rgba(255, 255, 255, 0.15)'
                                  : (isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.08)'))
                            }
                          }}
                        >
                          {range}
                        </Button>
                      ))}
                    </Box>

                    {/* Stats - macOS style list */}
                    <Box sx={{
                      bgcolor: theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(255, 255, 255, 0.08)'
                        : (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'),
                      borderRadius: 3,
                      overflow: 'hidden',
                      mb: 3,
                      border: theme.palette.actualMode === 'wallpaper'
                        ? '1px solid rgba(255, 255, 255, 0.15)'
                        : (isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : 'none')
                    }}>
                      {/* Total Chats */}
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        borderBottom: theme.palette.actualMode === 'wallpaper'
                          ? '1px solid rgba(255, 255, 255, 0.1)'
                          : (isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.06)')
                      }}>
                        <Typography variant="body2" sx={{
                          fontWeight: 500,
                          color: theme.palette.actualMode === 'wallpaper'
                            ? '#FFFFFF'
                            : (isDarkMode ? '#FFFFFF' : '#000000')
                        }}>
                          Total Chats
                        </Typography>
                        <Typography variant="h6" sx={{
                          fontWeight: 600,
                          color: theme.palette.actualMode === 'wallpaper'
                            ? '#FFFFFF'
                            : ACCENT_COLOR
                        }}>
                          {statistics.data.totalChats}
                        </Typography>
                      </Box>

                      {/* Total Messages */}
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        borderBottom: theme.palette.actualMode === 'wallpaper'
                          ? '1px solid rgba(255, 255, 255, 0.1)'
                          : (isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.06)')
                      }}>
                        <Typography variant="body2" sx={{
                          fontWeight: 500,
                          color: theme.palette.actualMode === 'wallpaper'
                            ? '#FFFFFF'
                            : (isDarkMode ? '#FFFFFF' : '#000000')
                        }}>
                          Total Messages
                        </Typography>
                        <Typography variant="h6" sx={{
                          fontWeight: 600,
                          color: theme.palette.actualMode === 'wallpaper'
                            ? '#FFFFFF'
                            : ACCENT_COLOR
                        }}>
                          {statistics.data.totalMessages}
                        </Typography>
                      </Box>

                      {/* Avg Messages */}
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2
                      }}>
                        <Typography variant="body2" sx={{
                          fontWeight: 500,
                          color: theme.palette.actualMode === 'wallpaper'
                            ? '#FFFFFF'
                            : (isDarkMode ? '#FFFFFF' : '#000000')
                        }}>
                          Avg Messages per Chat
                        </Typography>
                        <Typography variant="h6" sx={{
                          fontWeight: 600,
                          color: theme.palette.actualMode === 'wallpaper'
                            ? '#FFFFFF'
                            : ACCENT_COLOR
                        }}>
                          {statistics.data.averageMessagesPerChat.toFixed(1)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Most Active Day */}
                    {statistics.data.mostActiveDay && (
                      <Box sx={{
                        bgcolor: theme.palette.actualMode === 'wallpaper'
                          ? 'rgba(255, 255, 255, 0.08)'
                          : (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'),
                        borderRadius: 3,
                        p: 2,
                        mb: 3,
                        border: theme.palette.actualMode === 'wallpaper'
                          ? '1px solid rgba(255, 255, 255, 0.15)'
                          : (isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : 'none')
                      }}>
                        <Typography variant="caption" sx={{
                          display: 'block',
                          mb: 0.5,
                          color: theme.palette.actualMode === 'wallpaper'
                            ? 'rgba(255, 255, 255, 0.6)'
                            : (isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'),
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          letterSpacing: 0.5
                        }}>
                          Most Active Day
                        </Typography>
                        <Typography variant="body1" sx={{
                          fontWeight: 500,
                          color: theme.palette.actualMode === 'wallpaper'
                            ? '#FFFFFF'
                            : (isDarkMode ? '#FFFFFF' : '#000000')
                        }}>
                          {statistics.data.mostActiveDay}
                        </Typography>
                      </Box>
                    )}

                    {/* Most Used Apps */}
                    {statistics.data.mostUsedApps && statistics.data.mostUsedApps.length > 0 && (
                      <Box sx={{
                        bgcolor: theme.palette.actualMode === 'wallpaper'
                          ? 'rgba(255, 255, 255, 0.08)'
                          : (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'),
                        borderRadius: 3,
                        overflow: 'hidden',
                        border: theme.palette.actualMode === 'wallpaper'
                          ? '1px solid rgba(255, 255, 255, 0.15)'
                          : (isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : 'none')
                      }}>
                        <Typography variant="caption" sx={{
                          display: 'block',
                          p: 2,
                          pb: 1,
                          color: theme.palette.actualMode === 'wallpaper'
                            ? 'rgba(255, 255, 255, 0.6)'
                            : (isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'),
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          letterSpacing: 0.5
                        }}>
                          Most Used Apps
                        </Typography>
                        {statistics.data.mostUsedApps.map((app, index) => (
                          <Box key={index} sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 2,
                            borderTop: index === 0 ? 'none' : (theme.palette.actualMode === 'wallpaper'
                              ? '1px solid rgba(255, 255, 255, 0.1)'
                              : (isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.06)'))
                          }}>
                            <Typography variant="body2" sx={{
                              fontWeight: 500,
                              color: theme.palette.actualMode === 'wallpaper'
                                ? '#FFFFFF'
                                : (isDarkMode ? '#FFFFFF' : '#000000')
                            }}>
                              {app.name}
                            </Typography>
                            <Typography variant="body2" sx={{
                              fontWeight: 600,
                              color: theme.palette.actualMode === 'wallpaper'
                                ? 'rgba(255, 255, 255, 0.7)'
                                : (isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)')
                            }}>
                              {app.count} uses
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </>
                )}
              </DialogContent>
            </StyledDialog>
          </>
        )}

        {/* Edit Profile Dialog */}
        {editProfile.open && (
          <>
            <AnimatedBackdrop
              variants={backdropVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={handleEditProfileClose}
            />
            <StyledDialog
              open={true}
              onClose={handleEditProfileClose}
              maxWidth="sm"
              fullWidth
              BackdropProps={{ invisible: true }}
              PaperProps={{
                component: motion.div,
                variants: dialogVariants,
                initial: "hidden",
                animate: "visible",
                exit: "exit"
              }}
            >
              <DialogHeader>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Edit Profile
                </Typography>
                <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                  <IconButton
                    onClick={handleEditProfileClose}
                    edge="end"
                    sx={{
                      color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.6)',
                      '&:hover': {
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                      }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </motion.div>
              </DialogHeader>
              <DialogContent sx={{ p: 3, pt: 4, backgroundColor: 'transparent' }}>
                {/* Avatar Upload */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ position: 'relative' }}>
                    <Avatar
                      src={editProfile.photoPreview}
                      sx={{
                        width: 100,
                        height: 100,
                        cursor: 'pointer',
                        border: `3px solid ${ACCENT_COLOR}`
                      }}
                      onClick={() => document.getElementById('profile-photo-input').click()}
                    >
                      {!editProfile.photoPreview && <PersonIcon sx={{ fontSize: 50 }} />}
                    </Avatar>
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        bgcolor: ACCENT_COLOR,
                        borderRadius: '50%',
                        p: 0.5,
                        cursor: 'pointer'
                      }}
                      onClick={() => document.getElementById('profile-photo-input').click()}
                    >
                      <CameraAltIcon sx={{ color: '#fff', fontSize: 20 }} />
                    </Box>
                  </Box>
                  <input
                    id="profile-photo-input"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handlePhotoUpload}
                  />
                  <Typography variant="caption" sx={{
                    color: theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255, 255, 255, 0.7)'
                      : (isDarkMode ? '#8E8E93' : '#3C3C43'),
                    mt: 1
                  }}>
                    Click to upload photo (max 5MB)
                  </Typography>
                </Box>

                {/* Display Name */}
                <MacOSTextField
                  fullWidth
                  label="Display Name"
                  value={editProfile.displayName}
                  onChange={(e) => setEditProfile({ ...editProfile, displayName: e.target.value })}
                  sx={{ mb: 2 }}
                />

                {/* Email (read-only) */}
                <MacOSTextField
                  fullWidth
                  label="Email"
                  value={editProfile.email}
                  disabled
                  sx={{ mb: 3 }}
                  helperText="Email can be changed in Email Settings"
                />

                {/* Error */}
                {editProfile.error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {editProfile.error}
                  </Alert>
                )}

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleEditProfileClose}
                    sx={{
                      borderRadius: 20,
                      p: 1.5,
                      textTransform: 'none',
                      fontWeight: 500,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <motion.div style={{ width: '100%' }} whileTap={buttonTap}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleUpdateProfile}
                      disabled={editProfile.loading}
                      sx={{
                        bgcolor: ACCENT_COLOR,
                        color: '#FFFFFF',
                        borderRadius: 20,
                        p: 1.5,
                        textTransform: 'none',
                        fontWeight: 500,
                        boxShadow: 'none',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: '#0271E0',
                          boxShadow: 'none',
                          transform: 'translateY(-1px)'
                        }
                      }}
                    >
                      {editProfile.loading ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
                    </Button>
                  </motion.div>
                </Box>
              </DialogContent>
            </StyledDialog>
          </>
        )}

        {/* Confirmation Dialog */}
        {confirmDialog.open && (
          <>
            <AnimatedBackdrop
              variants={backdropVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={handleConfirmDialogClose}
            />
            <StyledDialog
              open={true}
              onClose={handleConfirmDialogClose}
              maxWidth="xs"
              fullWidth
              BackdropProps={{ invisible: true }}
              PaperProps={{
                component: motion.div,
                variants: dialogVariants,
                initial: "hidden",
                animate: "visible",
                exit: "exit"
              }}
            >
              <DialogHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon sx={{ color: confirmDialog.isDangerous ? '#FF453A' : ACCENT_COLOR }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {confirmDialog.title}
                  </Typography>
                </Box>
                <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                  <IconButton
                    onClick={handleConfirmDialogClose}
                    edge="end"
                    sx={{
                      color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.6)',
                      '&:hover': {
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                      }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </motion.div>
              </DialogHeader>
              <DialogContent sx={{ p: 3, pt: 4, backgroundColor: 'transparent' }}>
                <Typography variant="body2" sx={{
                  color: theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.85)'
                    : (isDarkMode ? '#8E8E93' : '#3C3C43'),
                  mb: 3
                }}>
                  {confirmDialog.message}
                </Typography>
                {confirmDialog.confirmText && (
                  <MacOSTextField
                    fullWidth
                    label={`Type "${confirmDialog.confirmText}" to confirm`}
                    id="confirm-input"
                    sx={{ mb: 2 }}
                  />
                )}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleConfirmDialogClose}
                    sx={{
                      borderRadius: 20,
                      p: 1.5,
                      textTransform: 'none',
                      fontWeight: 500,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    {confirmDialog.cancelText}
                  </Button>
                  <motion.div style={{ width: '100%' }} whileTap={buttonTap}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => {
                        const input = document.getElementById('confirm-input');
                        const value = input ? input.value : '';
                        handleConfirmDialogConfirm(value);
                      }}
                      sx={{
                        bgcolor: confirmDialog.isDangerous ? '#FF453A' : ACCENT_COLOR,
                        color: '#FFFFFF',
                        borderRadius: 20,
                        p: 1.5,
                        textTransform: 'none',
                        fontWeight: 500,
                        boxShadow: 'none',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: confirmDialog.isDangerous ? '#D93730' : '#0271E0',
                          boxShadow: 'none',
                          transform: 'translateY(-1px)'
                        }
                      }}
                    >
                      Confirm
                    </Button>
                  </motion.div>
                </Box>
              </DialogContent>
            </StyledDialog>
          </>
        )}
      </AnimatePresence>

      {/* Snackbar for messages */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ScrollContainer>
  );
};

export default ProfileDashboard;